
import React, { useEffect, useState } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import PublicOfferView from '@/components/PublicOfferView';
import { InsuranceAnalysisResult } from '@/types/insurance';
import { Loader2, Download, FileText, ExternalLink } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PDFViewer } from '@/components/PDFViewer';
import { InsuranceDetailsPanel } from '@/components/InsuranceDetailsPanel';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import '../styles/rich-message.css';

const PublicComparisonViewPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const params = useParams();
  const comparisonId = params.id || searchParams.get('id');
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<{
    title: string;
    files: Array<{
      id: string;
      fileUrl: string | null;
      analysisResult?: InsuranceAnalysisResult | null;
      filename: string;
    }>;
    companyName?: string;
    companyLogo?: string | null;
    comparisonContent?: string;
    companyColors?: {
      primary?: string;
      secondary?: string;
      accent?: string;
    };
  } | null>(null);

  useEffect(() => {
    async function fetchComparisonData() {
      if (!comparisonId) {
        setError('Keine Vergleichs-ID gefunden');
        setIsLoading(false);
        return;
      }

      try {
        console.log("Fetching comparison with sharing ID:", comparisonId);
        
        // Get comparison data
        const { data: comparison, error: comparisonError } = await supabase
          .from('insurance_comparisons')
          .select(`
            id,
            title,
            file_ids,
            analysis_results,
            user_id,
            comparison_content,
            status
          `)
          .eq('sharing_id', comparisonId)
          .single();

        if (comparisonError || !comparison) {
          console.error('Error fetching comparison:', comparisonError);
          setError('Vergleich nicht gefunden');
          setIsLoading(false);
          return;
        }
        
        if (comparison.status === 'processing') {
          setError('Vergleich wird noch verarbeitet. Bitte versuchen Sie es später erneut.');
          setIsLoading(false);
          return;
        }

        // Get company info and branding
        const { data: profileData, error: profileError } = await supabase
          .from('company_settings')
          .select('company_name, logo_url, primary_color, secondary_color, accent_color')
          .eq('user_id', comparison.user_id)
          .single();

        // Make sure we have valid profile data
        const companyName = profileError ? undefined : profileData?.company_name;
        const companyLogo = profileError ? null : profileData?.logo_url;
        const companyColors = profileError ? undefined : {
          primary: profileData?.primary_color || '#18B08D',
          secondary: profileData?.secondary_color || '#1A1F2C',
          accent: profileData?.accent_color || '#7E69AB'
        };

        // Get file data for all files in the comparison
        const fileIds = Array.isArray(comparison.file_ids) ? comparison.file_ids : [];
        const filesData = [];

        for (let i = 0; i < fileIds.length; i++) {
          const fileId = fileIds[i];
          const analysisResult = comparison.analysis_results && comparison.analysis_results[i];

          // Get file data
          const { data: file, error: fileError } = await supabase
            .from('files')
            .select('*')
            .eq('id', fileId)
            .single();

          if (fileError) {
            console.error(`Error fetching file ${fileId}:`, fileError);
            continue;
          }

          // Get file URL
          const { data: urlData } = supabase
            .storage
            .from(file.bucket_id)
            .getPublicUrl(file.storage_path);

          filesData.push({
            id: file.id,
            fileUrl: urlData.publicUrl,
            analysisResult: analysisResult as unknown as InsuranceAnalysisResult,
            filename: file.filename
          });
        }

        setComparisonData({
          title: comparison.title,
          files: filesData,
          companyName,
          companyLogo,
          comparisonContent: comparison.comparison_content,
          companyColors
        });
      } catch (error) {
        console.error('Error:', error);
        setError('Ein Fehler ist aufgetreten');
      } finally {
        setIsLoading(false);
      }
    }

    fetchComparisonData();
  }, [comparisonId]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <h2 className="text-xl font-medium text-gray-700">Vergleich wird geladen...</h2>
        </div>
      </div>
    );
  }

  if (error || !comparisonData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto px-4">
          <h2 className="text-2xl font-bold text-red-600 mb-2">Fehler</h2>
          <p className="text-gray-600">{error || 'Ein unbekannter Fehler ist aufgetreten'}</p>
        </div>
      </div>
    );
  }

  // If single file or direct render of HTML content
  if (comparisonData.files.length === 1 || comparisonData.comparisonContent) {
    if (comparisonData.comparisonContent) {
      // If we have generated HTML content, use it directly
      return (
        <div className="min-h-screen bg-white flex flex-col">
          {/* Company header */}
          {comparisonData.companyName && (
            <header className="w-full bg-gray-50 border-b border-gray-200 py-4 sticky top-0 z-10">
              <div className="container mx-auto px-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {comparisonData.companyLogo && (
                    <img 
                      src={comparisonData.companyLogo} 
                      alt={`${comparisonData.companyName} Logo`} 
                      className="h-10 w-auto object-contain"
                    />
                  )}
                  <h1 className="text-xl font-medium text-gray-800">{comparisonData.companyName}</h1>
                </div>
              </div>
            </header>
          )}

          {/* Making the content container have a fixed height and wrap with ScrollArea */}
          <div className="flex-grow flex overflow-hidden">
            <ScrollArea className="h-full w-full">
              <div 
                className="pt-6 pb-12 container mx-auto px-4 rich-content"
                dangerouslySetInnerHTML={{ __html: comparisonData.comparisonContent }}
              />
            </ScrollArea>
          </div>
        </div>
      );
    } else if (comparisonData.files.length === 1) {
      // Handle single file as offer view
      const file = comparisonData.files[0];
      return (
        <PublicOfferView
          title={comparisonData.title || file.filename}
          fileUrl={file.fileUrl}
          analysisResult={file.analysisResult}
          offerType="comparison"
          fileId={file.id}
          companyName={comparisonData.companyName}
          companyLogo={comparisonData.companyLogo}
        />
      );
    }
  }

  // Get brand colors from company profile, or use defaults
  const primaryColor = comparisonData.companyColors?.primary || '#18B08D';
  const secondaryColor = comparisonData.companyColors?.secondary || '#1A1F2C';
  const accentColor = comparisonData.companyColors?.accent || '#7E69AB';

  // Fallback styles if no HTML content is available
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {comparisonData.companyName && (
        <header className="w-full bg-gray-50 border-b border-gray-200 py-4 sticky top-0 z-10">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {comparisonData.companyLogo && (
                <img 
                  src={comparisonData.companyLogo} 
                  alt={`${comparisonData.companyName} Logo`} 
                  className="h-10 w-auto object-contain"
                />
              )}
              <h1 className="text-xl font-medium text-gray-800">{comparisonData.companyName}</h1>
            </div>
          </div>
        </header>
      )}

      {/* Fix the scrolling by making the content wrap with a fixed height ScrollArea */}
      <div className="flex-grow flex overflow-hidden">
        <ScrollArea className="h-full w-full">
          <div className="container mx-auto px-4 py-8">
            <div className="mb-8">
              <h1 
                className="text-3xl font-bold mb-6" 
                style={{ color: primaryColor }}
              >
                {comparisonData.title}
              </h1>
              
              <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
                <h2 className="text-xl font-semibold mb-4">Versicherungsvergleich</h2>
                <p className="text-gray-700 mb-4">
                  Dieser Vergleich umfasst {comparisonData.files.length} verschiedene Versicherungsangebote. 
                  Sie können die Details jedes Angebots unten einsehen und vergleichen.
                </p>
              </div>

              {/* Comparison Matrix Table */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100 overflow-x-auto">
                <h2 className="text-xl font-semibold mb-4">Leistungsvergleich</h2>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Leistungsmerkmal</TableHead>
                      {comparisonData.files.map((file, index) => (
                        <TableHead key={file.id}>
                          {file.filename.length > 20 
                            ? file.filename.substring(0, 20) + '...' 
                            : file.filename}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Premium row */}
                    <TableRow>
                      <TableCell className="font-medium">Preis</TableCell>
                      {comparisonData.files.map((file) => (
                        <TableCell key={`${file.id}-premium`}>
                          {file.analysisResult?.premium || "Nicht verfügbar"}
                        </TableCell>
                      ))}
                    </TableRow>
                    
                    {/* Coverage row */}
                    <TableRow>
                      <TableCell className="font-medium">Deckungsumfang</TableCell>
                      {comparisonData.files.map((file) => (
                        <TableCell key={`${file.id}-coverage`}>
                          {file.analysisResult?.coverage 
                            ? file.analysisResult.coverage.length > 50 
                              ? file.analysisResult.coverage.substring(0, 50) + '...' 
                              : file.analysisResult.coverage 
                            : "Nicht verfügbar"}
                        </TableCell>
                      ))}
                    </TableRow>
                    
                    {/* Duration row */}
                    <TableRow>
                      <TableCell className="font-medium">Vertragslaufzeit</TableCell>
                      {comparisonData.files.map((file) => (
                        <TableCell key={`${file.id}-duration`}>
                          {file.analysisResult?.duration || "Nicht verfügbar"}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>

            <Tabs defaultValue={comparisonData.files[0].id} className="w-full mb-8">
              <TabsList className="w-full flex overflow-x-auto">
                {comparisonData.files.map((file, index) => (
                  <TabsTrigger 
                    key={file.id} 
                    value={file.id}
                    className="flex-1 min-w-[150px]"
                    style={{
                      '--tab-accent': index === 0 ? primaryColor : 
                                    index === 1 ? accentColor : 
                                    `hsl(${210 + index * 30}, 70%, 50%)`
                    } as React.CSSProperties}
                  >
                    {file.filename.length > 20 
                      ? file.filename.substring(0, 20) + '...' 
                      : file.filename}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {comparisonData.files.map((file) => (
                <TabsContent key={file.id} value={file.id} className="mt-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <div>
                      <InsuranceDetailsPanel
                        title={file.filename}
                        analysisResult={file.analysisResult}
                        onDownload={() => {
                          if (file.fileUrl) window.open(file.fileUrl, '_blank');
                        }}
                        language="de"
                      />
                    </div>

                    {file.fileUrl && (
                      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden h-[600px]">
                        <PDFViewer url={file.fileUrl} onDownload={() => window.open(file.fileUrl, '_blank')} />
                      </div>
                    )}
                  </div>
                </TabsContent>
              ))}
            </Tabs>

            {/* File links section */}
            <div className="bg-white rounded-lg shadow-md p-6 mb-8 border border-gray-100">
              <h2 className="text-xl font-semibold mb-4">Dokumente</h2>
              <div className="space-y-3">
                {comparisonData.files.map((file) => (
                  <div key={file.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="h-5 w-5 text-gray-500" />
                      <span className="font-medium">{file.filename}</span>
                    </div>
                    {file.fileUrl && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => window.open(file.fileUrl!, '_blank')}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        Öffnen
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </div>
    </div>
  );
};

export default PublicComparisonViewPage;
